// ─────────────────────────────────────────────────────────────────────────────
// Pure transforms over structured medical history.
//
// Everything here is immutable — callers hold the result in React state or hand
// it to the repo. No I/O, no React, so it is directly unit-testable.
// See docs/requirements/MEDICAL_HISTORY_UI_GUIDE.md.
// ─────────────────────────────────────────────────────────────────────────────
import type { MedicalHistoryCategory } from '../api/generated/models/MedicalHistoryCategory';
import { MedicalHistoryField } from '../api/generated/models/MedicalHistoryField';
import type { PatientMedicalHistoryFieldDto } from '../api/generated/models/PatientMedicalHistoryFieldDto';
import type { MedicalHistoryFieldEntity, MedicalHistoryItemEntity } from '../types';
import {
  CatalogueEntry,
  FIELD_HEADINGS,
  FIELD_ORDER,
  isClinicianNote,
  isReservedCode,
  newClinicianNoteCode,
} from './medicalHistoryCatalog';

const itemKey = (category: MedicalHistoryCategory | string, code: string): string =>
  `${category}|${code}`;

/** All five boxes, empty. The API always returns five; local drafts need the same shape. */
export function emptyFields(): MedicalHistoryFieldEntity[] {
  return FIELD_ORDER.map((field) => ({
    Field: field,
    Label: FIELD_HEADINGS[field],
    HasStructuredData: false,
    Items: [],
    RenderedText: null,
    LegacyText: null,
  }));
}

/** DTO → local entity. Returns null when the API sent nothing at all. */
export function fieldsFromDto(
  dto: PatientMedicalHistoryFieldDto[] | null | undefined
): MedicalHistoryFieldEntity[] | null {
  if (!dto || dto.length === 0) return null;

  const mapped = dto
    .filter((f) => !!f.field)
    .map<MedicalHistoryFieldEntity>((f) => ({
      Field: f.field as MedicalHistoryField,
      Label: f.label ?? FIELD_HEADINGS[f.field as MedicalHistoryField] ?? null,
      HasStructuredData: f.hasStructuredData === true,
      RenderedText: f.renderedText ?? null,
      LegacyText: f.legacyText ?? null,
      Items: (f.items ?? [])
        .filter((i) => !!i.category && !!i.code)
        .map<MedicalHistoryItemEntity>((i) => ({
          Id: i.id,
          Category: i.category as MedicalHistoryCategory,
          Code: i.code as string,
          Label: i.label ?? null,
          Value: i.value ?? null,
          IsPresent: i.isPresent !== false,
          Source: i.source,
          FirstReportedAt: i.firstReportedAt ?? null,
          LastConfirmedAt: i.lastConfirmedAt ?? null,
        })),
    }));

  // Guarantee all five boxes exist even if the API trims one.
  const present = new Set(mapped.map((f) => f.Field));
  return [...mapped, ...emptyFields().filter((f) => !present.has(f.Field))].sort(
    (a, b) => FIELD_ORDER.indexOf(a.Field) - FIELD_ORDER.indexOf(b.Field)
  );
}

export function getField(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  field: MedicalHistoryField
): MedicalHistoryFieldEntity | undefined {
  return (fields ?? []).find((f) => f.Field === field);
}

export function presentItems(field: MedicalHistoryFieldEntity | undefined): MedicalHistoryItemEntity[] {
  return (field?.Items ?? []).filter((i) => i.IsPresent);
}

export function withdrawnItems(field: MedicalHistoryFieldEntity | undefined): MedicalHistoryItemEntity[] {
  return (field?.Items ?? []).filter((i) => !i.IsPresent);
}

/** Apply a change to one box, leaving the others untouched. */
function mapField(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  field: MedicalHistoryField,
  fn: (f: MedicalHistoryFieldEntity) => MedicalHistoryFieldEntity
): MedicalHistoryFieldEntity[] {
  const base = fields && fields.length ? fields : emptyFields();
  return base.map((f) => (f.Field === field ? fn(f) : f));
}

/** Stamp an item as touched on this device. Every mutator goes through here. */
const touch = (item: MedicalHistoryItemEntity): MedicalHistoryItemEntity => ({
  ...item,
  LocallyEdited: true,
  LastConfirmedAt: new Date().toISOString(),
});

/**
 * Insert or replace by (Category, Code). Taking over an existing row is
 * intentional — a clinician must be able to override a patient's "no" (§6).
 */
export function upsertItem(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  field: MedicalHistoryField,
  item: MedicalHistoryItemEntity
): MedicalHistoryFieldEntity[] {
  return mapField(fields, field, (f) => {
    const key = itemKey(item.Category, item.Code);
    const existingIndex = f.Items.findIndex((i) => itemKey(i.Category, i.Code) === key);
    if (existingIndex === -1) return { ...f, Items: [...f.Items, touch(item)] };
    const next = [...f.Items];
    next[existingIndex] = touch({ ...next[existingIndex], ...item });
    return { ...f, Items: next };
  });
}

export function toggleItem(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  field: MedicalHistoryField,
  category: MedicalHistoryCategory,
  code: string
): MedicalHistoryFieldEntity[] {
  const existing = getField(fields, field)?.Items.find(
    (i) => itemKey(i.Category, i.Code) === itemKey(category, code)
  );
  return upsertItem(fields, field, {
    ...(existing ?? { Category: category, Code: code, IsPresent: false }),
    IsPresent: !(existing?.IsPresent ?? false),
  });
}

export function setItemValue(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  field: MedicalHistoryField,
  category: MedicalHistoryCategory,
  code: string,
  value: string
): MedicalHistoryFieldEntity[] {
  return mapField(fields, field, (f) => ({
    ...f,
    Items: f.Items.map((i) =>
      itemKey(i.Category, i.Code) === itemKey(category, code) ? touch({ ...i, Value: value }) : i
    ),
  }));
}

/** Add a catalogued answer. Returns fields unchanged for a reserved code (§5e). */
export function addCatalogued(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  entry: CatalogueEntry,
  value?: string
): MedicalHistoryFieldEntity[] {
  if (isReservedCode(entry.code)) return fields && fields.length ? fields : emptyFields();
  return upsertItem(fields, entry.field, {
    Category: entry.category,
    Code: entry.code,
    Label: entry.label,
    Value: value ?? null,
    IsPresent: true,
  });
}

/**
 * Add free text. The minted code is stable for the life of the entry so
 * re-posting the same backup does not duplicate the note (§4) — which is why it
 * must be persisted with the draft, never regenerated on render.
 */
export function addFreeText(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  field: MedicalHistoryField,
  category: MedicalHistoryCategory,
  text: string
): MedicalHistoryFieldEntity[] {
  const trimmed = text.trim();
  if (!trimmed) return fields && fields.length ? fields : emptyFields();
  return mapField(fields, field, (f) => ({
    ...f,
    Items: [
      ...f.Items,
      touch({
        Category: category,
        // Minted once, here, at the moment the clinician commits the entry.
        // Never regenerated — a fresh code on each retry duplicates the note.
        Code: newClinicianNoteCode(),
        Label: trimmed,
        Value: trimmed,
        IsPresent: true,
      }),
    ],
  }));
}

/**
 * Withdraw rather than delete. A previously disclosed condition that silently
 * disappears is a clinical safety problem (§4).
 */
export function withdrawItem(
  fields: MedicalHistoryFieldEntity[] | null | undefined,
  field: MedicalHistoryField,
  category: MedicalHistoryCategory,
  code: string
): MedicalHistoryFieldEntity[] {
  return mapField(fields, field, (f) => ({
    ...f,
    Items: f.Items.map((i) =>
      itemKey(i.Category, i.Code) === itemKey(category, code) ? touch({ ...i, IsPresent: false }) : i
    ),
  }));
}

/**
 * Merge server history into the local copy.
 *
 * The server is authoritative for provenance (Id, Label, Source, timestamps) and
 * for rows this device has never seen. A locally-edited item keeps its own
 * IsPresent/Value, because that is the clinician's pending change and a
 * background prefetch must not revert it.
 *
 * Locally-edited items the server did not return are KEPT. The device's read
 * path returns positives only, so a withdrawal we made is absent from the
 * response — dropping it here would make the withdrawn item silently reappear.
 */
export function mergeStructuredHistory(
  local: MedicalHistoryFieldEntity[] | null | undefined,
  api: MedicalHistoryFieldEntity[] | null | undefined
): MedicalHistoryFieldEntity[] | null {
  if (!api || api.length === 0) return local ?? null;
  if (!local || local.length === 0) return api;

  return api.map((apiField) => {
    const localField = local.find((f) => f.Field === apiField.Field);
    if (!localField) return apiField;

    const localByKey = new Map(localField.Items.map((i) => [itemKey(i.Category, i.Code), i]));

    const reconciled = apiField.Items.map((apiItem) => {
      const localItem = localByKey.get(itemKey(apiItem.Category, apiItem.Code));
      localByKey.delete(itemKey(apiItem.Category, apiItem.Code));
      if (!localItem?.LocallyEdited) return apiItem;
      return {
        ...apiItem, // server provenance
        IsPresent: localItem.IsPresent,
        Value: localItem.Value,
        LocallyEdited: true,
      };
    });

    // Whatever is left in the map is local-only: keep the edits, drop stale
    // untouched rows the server no longer reports.
    const localOnlyEdits = Array.from(localByKey.values()).filter((i) => i.LocallyEdited);

    return {
      ...apiField, // server owns HasStructuredData / RenderedText / LegacyText
      Items: [...reconciled, ...localOnlyEdits],
    };
  });
}

/** One entry as the sync payload wants it — guide §4. */
export interface MedicalHistoryEntryPayload {
  Category: MedicalHistoryCategory;
  Code: string;
  Value: string | null;
  IsPresent: boolean;
}

export interface MedicalHistoryFieldPayload {
  Field: MedicalHistoryField;
  Items: MedicalHistoryEntryPayload[];
}

/**
 * Reduce the local history to what may be sent.
 *
 * Only locally-edited items go up. Everything sent is filed as
 * `source: Clinician` and a patient pack can never overwrite a Clinician row,
 * so echoing back untouched PatientPack answers would permanently take them
 * away from the patient.
 *
 * Returns null when nothing was touched, so the payload stays byte-identical
 * to today's until a clinician actually edits something.
 */
export function medicalHistoryForSync(
  fields: MedicalHistoryFieldEntity[] | null | undefined
): MedicalHistoryFieldPayload[] | null {
  if (!fields || fields.length === 0) return null;

  const payload = fields
    .map<MedicalHistoryFieldPayload>((f) => ({
      Field: f.Field,
      Items: f.Items.filter(isSendable).map((i) => ({
        Category: i.Category,
        Code: i.Code,
        Value: i.Value ?? null,
        IsPresent: i.IsPresent,
      })),
    }))
    .filter((f) => f.Items.length > 0);

  return payload.length ? payload : null;
}

function isSendable(item: MedicalHistoryItemEntity): boolean {
  if (!item.LocallyEdited) return false;
  // An empty code makes the server mint a fresh GUID on every retry, and an
  // offline app retries by design — that duplicates the note each time (§4).
  if (!item.Code || !item.Code.trim()) return false;
  // Writing these overwrites the patient's own pack note (§5e).
  if (isReservedCode(item.Code)) return false;
  // Screening entries are fixed pack questions; free text there is a condition.
  if (item.Category === 'Screening' && isClinicianNote(item.Code)) return false;
  return true;
}

/** Everything the clinician has entered, for a quick "has anything changed" check. */
export function totalItemCount(fields: MedicalHistoryFieldEntity[] | null | undefined): number {
  return (fields ?? []).reduce((sum, f) => sum + f.Items.length, 0);
}
